import {
  AuthProvider,
  ensureAuthenticated,
  FeatureFlagProvider,
  getCurrentUserControllerGetCurrentUserQueryOptions,
} from '@artic/api-client';
import { useFeatureFlag } from '@artic/ui/feature-flag';
import { FullHeightStack } from '@artic/ui/full-height-layout';
import { clearAuthCookies } from '@artic/ui/lib/auth-cookies';
import { PageHeader } from '@artic/ui/page-header';
import { SidebarInset, SidebarProvider } from '@artic/ui/sidebar';
import { Toaster } from '@artic/ui/toast';
import { TooltipProvider } from '@artic/ui/tooltip';
import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, Outlet, useRouterState } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Suspense, type ReactNode } from 'react';

import type { AuthState } from '../auth';
import { getAuthState, redirectToHub } from '../auth';
import { IntercomIdentity } from '../components/intercom-identity';
import { QualitySidebar } from '../components/quality-sidebar';
import { RouteBreadcrumbs } from '../components/route-breadcrumbs';
import { RouteHeaderActions } from '../components/route-header-actions';
import { SentryIdentity } from '../components/sentry-identity';
import { ThemeProvider } from '../components/theme-provider';
import { usePartnerId } from '../features/inbox/use-partner-name';
import { RealtimeProvider } from '../features/realtime/realtime-provider';
import { usePartnerRealtimeSync } from '../features/realtime/use-partner-realtime-sync';

export interface RouterContext {
  queryClient: QueryClient;
  auth: AuthState;
}

const COOKIE_OPTIONS = (() => {
  const domain = import.meta.env['VITE_AUTH_COOKIE_DOMAIN'] as string | undefined;
  return domain ? { domain } : {};
})();

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context: { queryClient } }) => {
    // Read fresh cookie state on every navigation instead of relying on the
    // static snapshot captured at router creation time.
    let auth = getAuthState();

    if (!auth.isAuthenticated) {
      // Neither access token nor refresh token — redirect immediately.
      redirectToHub();
    }

    if (!auth.token) {
      // Access token is missing but refresh token exists — attempt a
      // proactive refresh so the AuthProvider's /current-user call
      // doesn't need the 401 → refresh → retry round-trip.
      const ok = await ensureAuthenticated();
      if (!ok) redirectToHub();

      // Re-read after refresh wrote new cookies.
      auth = getAuthState();
    }

    // Validate the session server-side before rendering. A stored token can
    // outlive its user row (e.g. after `supabase db reset --local`); without
    // this gate the AuthProvider's suspense query throws 404 and the app gets
    // stuck on the root CatchBoundary with no path back to login.
    try {
      await queryClient.ensureQueryData(getCurrentUserControllerGetCurrentUserQueryOptions());
    } catch (error) {
      console.warn('[auth] /current-user rejected, clearing session', error);
      clearAuthCookies(COOKIE_OPTIONS);
      redirectToHub();
    }

    return { auth };
  },
  component: RootLayout,
});

function AuthLoading() {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <p className="text-muted-foreground text-sm">Loading…</p>
    </div>
  );
}

function useLayoutMode() {
  return useRouterState({
    select: (state) => {
      for (let i = state.matches.length - 1; i >= 0; i--) {
        const layout: unknown = state.matches[i]?.staticData.layout;
        if (typeof layout === 'string') return layout;
      }
      return 'default' as const;
    },
  });
}

function FeatureFlagProviderWithPartner({ children }: { children: ReactNode }) {
  const partnerId = usePartnerId();
  return <FeatureFlagProvider partnerId={partnerId}>{children}</FeatureFlagProvider>;
}

/**
 * App-wide bridge: subscribes the current user to their own partner's realtime
 * topic for the whole session (gated on `quality_realtime`). When an admin
 * flips this partner's flags, a `partner_changed` signal refetches the flags so
 * the sidebar nav updates without a reload. Renders nothing. Must sit inside
 * `RealtimeProvider` (for the socket); `AuthProvider` + `FeatureFlagProvider`
 * are ancestors, so `usePartnerId`/`useFeatureFlag` resolve.
 */
function PartnerRealtimeSync() {
  const partnerId = usePartnerId();
  const enabled = useFeatureFlag('quality_realtime');
  usePartnerRealtimeSync(partnerId, { enabled });
  return null;
}

function RootLayout() {
  const { auth } = Route.useRouteContext();
  const layoutMode = useLayoutMode();

  return (
    <ThemeProvider>
      <TooltipProvider>
        <Suspense fallback={<AuthLoading />}>
          <AuthProvider activePartner={auth.partner}>
            <IntercomIdentity />
            <SentryIdentity />
            <FeatureFlagProviderWithPartner>
              <RealtimeProvider>
                <PartnerRealtimeSync />
                <SidebarProvider>
                  <QualitySidebar />
                  {/*
                   * Full-height routes (Gantt timeline, AG Grid, inbox) scroll
                   * internally, so the inset needs a definite viewport height: the
                   * h-16 PageHeader takes its 64px and FullHeightStack fills the
                   * rest. The sidebar wrapper only supplies `min-h-svh` (a minimum),
                   * so without a fixed height the inset grows with its content and
                   * the page spills past the viewport by the header's height instead
                   * of the inner region scrolling. `overflow-y-auto` (not hidden) so
                   * the odd full-height route that is a tall document (e.g. the
                   * operation editor) still scrolls rather than being clipped; the
                   * internal-scroll routes fit exactly and never show this scrollbar.
                   * Default routes keep ordinary document scroll.
                   */}
                  <SidebarInset
                    className={layoutMode === 'full-height' ? 'h-svh overflow-y-auto' : undefined}
                  >
                    <PageHeader actions={<RouteHeaderActions />}>
                      <RouteBreadcrumbs />
                    </PageHeader>
                    {layoutMode === 'full-height' ? (
                      <FullHeightStack>
                        <Outlet />
                      </FullHeightStack>
                    ) : (
                      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
                        <Outlet />
                      </div>
                    )}
                  </SidebarInset>
                </SidebarProvider>
              </RealtimeProvider>
            </FeatureFlagProviderWithPartner>
          </AuthProvider>
        </Suspense>
        <Toaster position="top-right" richColors closeButton />
        {import.meta.env.DEV && (
          <>
            <ReactQueryDevtools buttonPosition="bottom-left" />
            <TanStackRouterDevtools position="bottom-right" />
          </>
        )}
      </TooltipProvider>
    </ThemeProvider>
  );
}
